<template>
  <v-card flat>
    <v-card-title>Add Mileage</v-card-title>
    <v-card-text>
      <v-form @submit.prevent="submitForm">
        <v-row>
          <v-col cols="12" md="4">
            <v-select
              v-model="form.adi"
              :items="adiList"
              item-value="adi_id"
              label="Driving Instructor"
              :error-messages="errors.adi"
              @blur="$v.form.adi.$touch()"
              @change="
                $v.form.adi.$touch();
                loadADIMileage();
              "
              outlined
              dense
              clearable
            >
              <template v-slot:item="{ item }">
                <strong> A{{ item.adi_id }}</strong> &nbsp;-&nbsp;
                {{ item.title }}
                {{ item.fname }}
                {{ item.lname }}
              </template>
              <template v-slot:selection="{ item }">
                <strong> A{{ item.adi_id }}</strong
                >&nbsp;-&nbsp; {{ item.title }}
                {{ item.fname }}
                {{ item.lname }}
              </template>
            </v-select>
          </v-col>

          <v-col cols="12" md="4">
            <v-text-field
              v-model="form.date"
              label="Date *"
              type="date"
              :error-messages="errors.date"
              @blur="$v.form.date.$touch()"
              outlined
              dense
            ></v-text-field>
          </v-col>

          <v-col cols="12" md="4">
            <v-select
              v-model="form.type"
              :items="mileageTypes"
              label="Mileage Type *"
              :error-messages="errors.type"
              @blur="$v.form.type.$touch()"
              @change="$v.form.type.$touch()"
              outlined
              dense
            ></v-select>
          </v-col>

          <v-col cols="12" md="6">
            <v-text-field
              v-model="form.startMileage"
              label="Start Mileage *"
              type="number"
              placeholder="Vehicle odometer reading at the start of the trip"
              min="0"
              :error-messages="errors.startMileage"
              @blur="$v.form.startMileage.$touch()"
              outlined
              dense
            ></v-text-field>
          </v-col>

          <v-col cols="12" md="6">
            <v-text-field
              v-model="form.endMileage"
              label="End Mileage *"
              type="number"
              placeholder="Vehicle odometer reading upon reaching the end of the trip"
              min="0"
              :error-messages="errors.endMileage"
              @blur="$v.form.endMileage.$touch()"
              outlined
              dense
            ></v-text-field>
          </v-col>

          <v-col cols="12" v-if="form.startMileage && form.endMileage">
            <v-alert type="info" outlined>
              <strong>Total Distance:</strong> {{ totalMileage }} miles
            </v-alert>
          </v-col>
        </v-row>

        <v-btn
          type="submit"
          color="info"
          :loading="loading"
          :disabled="$v.form.$invalid"
        >
          <v-icon left>mdi-plus</v-icon>
          Add Mileage
        </v-btn>
      </v-form>
    </v-card-text>
    <v-divider class="mx-4"></v-divider>
    <v-card-title v-if="form.adi"
      >Mileage Records - {{ getADIName }}</v-card-title
    >
    <v-card-title v-else>Mileage Records - School</v-card-title>
    <v-card-text>
      <TaxYearSelector
        @export="exportTransaction"
        :exportFomatsList="expFomatsList"
      />
    </v-card-text>
    <v-card-text>
      <v-data-table
        :headers="headers"
        :items="showSchoolData"
        :search="keyword"
        class="elevation-1"
        :loading="loading"
        loading-text="Loading... Please wait"
      >
        <template v-slot:[`item.date`]="{ item }">
          <span style="text-transform: capitalize">{{
            item.date | dateReOrder
          }}</span>
        </template>
        <template v-slot:[`item.adi_name`]="{ item }">
          <span v-if="item.adi_name"
            >{{ item.adi_name }} (A{{ item.adi_id }})</span
          ><span v-else>{{ getADIName }}</span>
        </template>
        <template v-slot:[`item.start_mileage`]="{ item }">
          <span>{{ item.start_mileage | numStyle }}</span>
        </template>
        <template v-slot:[`item.end_mileage`]="{ item }">
          <span>{{ item.end_mileage | numStyle }}</span>
        </template>
        <template v-slot:[`item.journey`]="{ item }">
          <span>{{ (item.end_mileage - item.start_mileage) | numStyle }}</span>
        </template>
        <template v-slot:[`item.type`]="{ item }">
          <span style="text-transform: capitalize">{{ item.type }}</span>
        </template>
      </v-data-table>
    </v-card-text>
  </v-card>
</template>

<script>
import { required, minValue } from "vuelidate/lib/validators";
import { mapActions, mapGetters } from "vuex";
import {
  M_OU,
  GET_ACTIVE_INSTRUCTORS,
  SAVE_FM_MILAGE,
  LOAD_FM_MILAGE,
  IS_SCHOOL_FM_MILAGE_LOADED,
  GET_SCHOOL_FM_MILAGE,
  LOAD_SCHOOL_FM_MILAGE,
  DOWNLOAD_EXPORT_FILE,
} from "@/store/mutation-list";
import TaxYearSelector from "@/components/FinancialManagement/TaxYearSelector.vue";

// Custom validator for end mileage
const greaterThanStart = (value, vm) => {
  return (
    !value ||
    !vm.startMileage ||
    parseFloat(value) > parseFloat(vm.startMileage)
  );
};

export default {
  name: "MileageForm",
  components: { TaxYearSelector },

  data() {
    return {
      loading: false,
      form: {
        adi: "",
        date: "",
        startMileage: "",
        endMileage: "",
        type: "",
      },
      mileageTypes: [
        { text: "Business", value: "business" },
        { text: "Personal", value: "personal" },
        {
          text: "Commute (To and from a fixed place of work.)",
          value: "commute",
        },
      ],
      tblLoading: false,
      keyword: "", // the seaching function,
      headers: [
        { text: "Date", value: "mileage_date" },
        { text: "ADI", value: "adi_name" },
        { text: "Start", value: "start_mileage" },
        { text: "End", value: "end_mileage" },
        { text: "Mileage of Journey", value: "journey" },
        { text: "Type", value: "type" },
        // { text: "Action", value: "" },
      ],
      adiMileageData: [],
      expFomatsList: ["CSV", "Excel"],
    };
  },

  validations: {
    form: {
      adi: {
        required,
      },
      date: {
        required,
      },
      startMileage: {
        required,
        minValue: minValue(0),
      },
      endMileage: {
        required,
        minValue: minValue(0),
        greaterThanStart,
      },
      type: {
        required,
      },
    },
  },

  computed: {
    ...mapGetters({
      adiList: `${M_OU}${GET_ACTIVE_INSTRUCTORS}`,
      isSchoolMielageLoaded: `${M_OU}${IS_SCHOOL_FM_MILAGE_LOADED}`,
      schoolMilage: `${M_OU}${GET_SCHOOL_FM_MILAGE}`,
    }),
    showSchoolData() {
      if (!this.form.adi) {
        return this.schoolMilage;
      } else {
        return this.adiMileageData;
      }
    },
    getADIName() {
      if (this.form.adi && this.adiList.length > 0) {
        const adi_details = this.adiList.find(
          (adi) => adi.adi_id === this.form.adi
        );
        return ` ${adi_details.fname} ${adi_details.lname} (A${this.form.adi})`;
      }
      return "";
    },
    errors() {
      const errors = {
        adi: [],
        date: [],
        startMileage: [],
        endMileage: [],
        type: [],
      };
      if (this.$v.form.adi.$dirty) {
        !this.$v.form.adi.required &&
          errors.adi.push("Please select a driving instructor.");
      }

      if (this.$v.form.date.$dirty) {
        !this.$v.form.date.required && errors.date.push("Date is required.");
      }

      if (this.$v.form.startMileage.$dirty) {
        !this.$v.form.startMileage.required &&
          errors.startMileage.push("Start mileage is required.");
        !this.$v.form.startMileage.minValue &&
          errors.startMileage.push("Start mileage cannot be negative.");
      }

      if (this.$v.form.endMileage.$dirty) {
        !this.$v.form.endMileage.required &&
          errors.endMileage.push("End mileage is required.");
        !this.$v.form.endMileage.minValue &&
          errors.endMileage.push("End mileage cannot be negative.");
        !this.$v.form.endMileage.greaterThanStart &&
          errors.endMileage.push(
            "End mileage must be greater than start mileage."
          );
      }

      if (this.$v.form.type.$dirty) {
        !this.$v.form.type.required &&
          errors.type.push("Mileage type is required.");
      }

      return errors;
    },

    totalMileage() {
      if (this.form.startMileage && this.form.endMileage) {
        const total =
          parseFloat(this.form.endMileage) - parseFloat(this.form.startMileage);
        return total > 0 ? total.toFixed(1) : 0;
      }
      return 0;
    },
  },

  methods: {
    ...mapActions({
      saveMilage: `${M_OU}${SAVE_FM_MILAGE}`,
      getMileage: `${M_OU}${LOAD_FM_MILAGE}`,
      loadSchoolMileage: `${M_OU}${LOAD_SCHOOL_FM_MILAGE}`,
      dwExportFile: `${M_OU}${DOWNLOAD_EXPORT_FILE}`,
    }),
    async submitForm() {
      this.$v.form.$touch();
      if (this.$v.form.$invalid) return;

      this.saveMilage({
        adi: this.form.adi,
        date: this.form.date,
        type: this.form.type,
        start: this.form.startMileage,
        end: this.form.endMileage,
      }).then((res) => {
        //response from store. All complete just need to show to popup message
        if (res == "SUCCESS") {
          this.$fire({
            title: "Success",
            text: "Category recorded successfuly.",
            type: "success",
            timer: 3000,
          }).then((r) => {
            console.log(r.value);
          });
          this.resetForm();
          this.dialog = false;
        } else {
          this.$fire({
            title: "Error",
            text: "Error recording category. Please try again.",
            type: "error",
            allowOutsideClick: false,
            //timer: 3000,
          }).then((r) => {
            console.log(r.value);
          });
        }
      });

      /*

      this.loading = true;

      try {
        const mileageData = {
          ...this.form,
          totalMileage: this.totalMileage,
        };

        // eslint-disable-next-line no-unused-vars
        const response = await axios.post("/api/mileage", mileageData);

        this.$emit("success", "Mileage record added successfully!");
        this.resetForm();

        // Optionally dispatch to Vuex store
        // this.$store.dispatch('mileage/addMileage', response.data)
      } catch (error) {
        console.error("Error adding mileage:", error);
        this.$emit("error", "Error adding mileage record. Please try again.");
      } finally {
        this.loading = false;
      }*/
    },
    loadADIMileage() {
      this.tblLoading = true;
      this.adiMileageData = [];
      let tempOBj = [];
      this.getMileage({
        adiid: this.form.adi,
      })
        .then((res) => {
          if (res) {
            tempOBj = res;
            // console.log(tempOBj);
            if ("result" in tempOBj) {
              this.adiMileageData = [];
            } else {
              this.adiMileageData = res;
            }
          } else {
            this.adiMileageData = [];
          }
        })
        .finally(() => {
          this.tblLoading = false;
        });
    },
    //this catches the export event
    exportTransaction({ start_date, end_date, exp_format }) {
      //lets send the reqirenment to store to get the file
      //reqirnments are
      //start date, end date, export format, records of school or adi, data type i.e. income,expense or milage
      let owner = "school";
      let ext = ".csv";

      if (this.form.adi != "" || this.form.adi != null) {
        owner = this.form.adi;
      }
      switch (exp_format) {
        case "CSV":
          ext = "export.csv";
          break;
        case "Excel":
          ext = "export.xlsx";
          break;
        case "PDF":
          ext = "export.pdf";
          break;

        default:
          ext = "export.csv";
          break;
      }

      this.dwExportFile({
        owner: owner,
        start: start_date,
        end: end_date,
        type: "mileage",
        exp_format: exp_format,
      })
        .then((rep) => {
          //create file link in the browser's memeory
          const href = URL.createObjectURL(rep);
          // create 'a' HTML element with the ref to the file & click
          const link = document.createElement("a");
          link.href = href;
          // link.setAttribute("download", "file.csv");
          // link.setAttribute("download", "file.xlsx");
          link.setAttribute("download", ext);
          document.body.appendChild(link);
          link.click();

          // clean up 'a' element and remove objectURL
          document.body.removeChild(link);
          URL.revokeObjectURL(href);
        })
        .finally(() => {});
    },

    resetForm() {
      const today = new Date().toISOString().substr(0, 10);
      this.form = {
        date: today,
        startMileage: "",
        endMileage: "",
        type: "",
      };
      this.adiMileageData = [];
      this.keyword = "";
      this.$v.form.$reset();
    },
  },

  mounted() {
    // Set default date to today
    const today = new Date().toISOString().substr(0, 10);
    this.form.date = today;

    // lets load all the data if not alreay loaded
    if (!this.isSchoolMielageLoaded) {
      this.loadSchoolMileage();
    }
  },
};
</script>

<style scoped>
.v-alert {
  margin-top: 10px;
}
</style>
